import React, { useContext, useState, useCallback } from 'react';
import { Typography, Col, Row, Tag, message, Button, Drawer, Badge } from 'antd';
import { AlertOutlined, HistoryOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  TyrePressureAlert,
  EngineTemperatureAlert,
  SpeedAlert,
  BatteryAlert,
  DoorOpenAlert,
  LampStatusAlert,
  VehicleStartStopAlert,
  CriticalMalfunctionAlert,
  SeatBeltAlert,
  EngineIdleAlert,
} from './AlertBlocks';
import LowFuelAlert from './AlertBlocks/LowFuelAlert';

const { Title, Text } = Typography;

const ALERT_COUNT = 11;

function relativeTime(ts) {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

const HistoryRow = ({ entry, index }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 0',
      borderBottom: '1px solid #f1f5f9',
      animation: `alert-hist-in 0.3s ease both ${index * 0.04}s`,
    }}
  >
    {/* status dot */}
    <div
      style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: entry.success ? '#16a34a' : '#ef4444',
        marginTop: 5,
      }}
    />

    {/* content */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
        {entry.name}
      </div>
      {entry.value && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{entry.value}</div>
      )}
    </div>

    {/* right: time + badge */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>{relativeTime(entry.timestamp)}</span>
      <Tag
        style={{
          margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, padding: '0 7px',
          background: entry.success ? '#f0fdf4' : '#fff5f5',
          border: `1px solid ${entry.success ? '#86efac' : '#fca5a5'}`,
          color: entry.success ? '#16a34a' : '#ef4444',
        }}
      >
        {entry.success
          ? <><CheckCircleOutlined /> Sent</>
          : <><CloseCircleOutlined /> Failed</>}
      </Tag>
    </div>
  </div>
);

const EmptyHistory = () => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 10, padding: '60px 24px',
    }}
  >
    <div
      style={{
        width: 52, height: 52, borderRadius: 13,
        background: '#f1f5f9', border: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, color: '#cbd5e1',
      }}
    >
      <HistoryOutlined />
    </div>
    <Text style={{ fontSize: 13, color: '#94a3b8' }}>No alerts triggered yet</Text>
    <Text style={{ fontSize: 11, color: '#cbd5e1', textAlign: 'center' }}>
      Trigger an alert to see it recorded here
    </Text>
  </div>
);

const Alerts = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [alertHistory, setAlertHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alert_history') || '[]'); } catch { return []; }
  });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Inject CSS once
  React.useEffect(() => {
    let el = document.getElementById('alert-hist-animations');
    if (!el) { el = document.createElement('style'); el.id = 'alert-hist-animations'; document.head.appendChild(el); }
    el.textContent = `
      @keyframes alert-hist-in {
        from { opacity: 0; transform: translateX(10px); }
        to   { opacity: 1; transform: translateX(0); }
      }
    `;
    return () => { document.getElementById('alert-hist-animations')?.remove(); };
  }, []);

  const addToHistory = useCallback((record) => {
    const entry = { ...record, timestamp: Date.now(), id: `${Date.now()}-${Math.random()}` };
    setAlertHistory((prev) => {
      const next = [entry, ...prev].slice(0, 100);
      localStorage.setItem('alert_history', JSON.stringify(next));
      return next;
    });
  }, []);

  const clearHistory = () => {
    setAlertHistory([]);
    localStorage.removeItem('alert_history');
  };

  const successCount = alertHistory.filter((e) => e.success).length;
  const failCount = alertHistory.length - successCount;

  return (
    <>
      {contextHolder}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: '#fff5f5', border: '1px solid #fca5a5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <AlertOutlined style={{ fontSize: 18, color: '#ef4444' }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Alerts</Title>
            <Text style={{ fontSize: 12, color: '#94a3b8' }}>Simulate and trigger vehicle alert conditions</Text>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag color="error" style={{ borderRadius: 20, fontSize: 11, padding: '3px 10px', margin: 0 }}>
            {ALERT_COUNT} alert types
          </Tag>
          <Badge count={alertHistory.length} size="small" offset={[-4, 4]}>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              style={{ borderRadius: 9 }}
            >
              History
            </Button>
          </Badge>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        {[
          [TyrePressureAlert, 'Low Tyre Pressure', (v) => v ? `${v} PSI` : null],
          [EngineTemperatureAlert, 'Engine High Temperature', (v) => v ? `${v} °C` : null],
          [SpeedAlert, 'Over Speed', (v) => `${v} km/h`],
          [BatteryAlert, 'Low Battery', (v) => v ? `${v}%` : null],
          [DoorOpenAlert, 'Door Open', () => null],
          [LampStatusAlert, 'Lamp Status', () => 'Headlamp ON'],
          [VehicleStartStopAlert, 'Vehicle Start / Stop', () => null],
          [CriticalMalfunctionAlert, 'Critical Malfunction', () => null],
          [SeatBeltAlert, 'Seat Belt Unfastened', () => null],
          [EngineIdleAlert, 'Engine Idle', (v) => `${v} mins`],
          [LowFuelAlert, 'Low Fuel', (v) => `${v}%`],
        ].map(([AlertComponent, name], i) => (
          <Col key={i} xs={24} sm={12} lg={8} xl={6} style={{ display: 'flex' }}>
            <div style={{ width: '100%' }}>
              <AlertComponent
                messageApi={messageApi}
                onTriggered={(record) => addToHistory({ name, ...record })}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HistoryOutlined style={{ color: '#64748b' }} />
            <span style={{ fontWeight: 700 }}>Alert History</span>
            {alertHistory.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginLeft: 4 }}>
                <Tag style={{ margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a' }}>
                  {successCount} sent
                </Tag>
                {failCount > 0 && (
                  <Tag style={{ margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#fff5f5', border: '1px solid #fca5a5', color: '#ef4444' }}>
                    {failCount} failed
                  </Tag>
                )}
              </div>
            )}
          </div>
        }
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        width={400}
        footer={
          alertHistory.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={clearHistory}
                style={{ borderRadius: 8 }}
              >
                Clear All
              </Button>
            </div>
          ) : null
        }
        styles={{ body: { padding: '0 20px' } }}
      >
        {alertHistory.length === 0 ? (
          <EmptyHistory />
        ) : (
          <div>
            {alertHistory.map((entry, i) => (
              <HistoryRow key={entry.id} entry={entry} index={i} />
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default Alerts;
